import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import EmployeeCard from '@/platform/common/components/EmployeeCard/EmployeeCard';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { localDate } from '@/platform/lib/utils';
import {
    Button,
    Container,
    FilterBarDropdown,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import {
    ArrowDownTrayIcon,
    CpuChipIcon,
    EllipsisVerticalIcon,
    EyeIcon,
} from '@heroicons/react/24/outline';
import { router, usePage } from '@inertiajs/react';
import { useState } from 'react';
import ChecksModal from './components/ChecksModal';
import StatusIcon from './components/StatusIcon';

const IndexView = () => {
    const {
        auth,
        dgr_identities: dgrIdentities,
        statuses,
        types,
    } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters(['dgr_identities']);

    const [checksModal, setChecksModal] = useState(null);

    const canDownload = auth?.permissions?.includes('download dgr screenshot');
    const canStartBot = auth?.permissions?.includes('start dgr bot');
    // The bot route is employee-only; an entrant id would resolve to an unrelated employee.
    const canCheckDgr = dgrIdentity =>
        canStartBot && dgrIdentity.identifiable_type === 'employees';

    const showEntity = (filters?.entity_ids?.length || 0) > 1;
    const columns = 5 + (showEntity ? 1 : 0) + (canDownload ? 1 : 0);

    const checkDgr = dgrIdentity =>
        router.post(
            `/customers/${auth?.customer?.id}/employees/${dgrIdentity.identifiable_id}/dgr/bot`,
            {},
            {
                preserveScroll: true,
                preserveState: true,
            }
        );

    return (
        <Container fluid>
            <Segment>
                <SegmentHeader
                    header={`Doelgroepregister (${dgrIdentities?.meta?.total || 0})`}
                />
                <SegmentContent>
                    <SearchBarFilter only={['dgr_identities']}>
                        <FilterBarDropdown
                            multiple
                            selectAll
                            label={`Entiteiten (${filters?.entity_ids?.length || 0}/${auth?.customer?.entities?.length || 0})`}
                            options={auth.customer?.entities?.map(entity => ({
                                id: entity.id,
                                name: entity.number
                                    ? `${entity.number}. ${entity.name}`
                                    : entity.name,
                            }))}
                            value={filters?.entity_ids || []}
                            onChange={value =>
                                updateFilters('entity_ids', value)
                            }
                        />
                        <FilterBarDropdown
                            label={`Type (${types?.find(type => type.id === filters?.type)?.name || ''})`}
                            options={types || []}
                            value={filters?.type || ''}
                            onChange={value => updateFilters('type', value)}
                        />
                        <FilterBarDropdown
                            clearable
                            label={`Laatst status (${statuses?.find(status => status.id === filters?.status)?.name || 'Alle'})`}
                            options={statuses || []}
                            value={filters?.status || ''}
                            onChange={value => updateFilters('status', value)}
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Medewerker</TableHeaderCell>
                                {showEntity && (
                                    <TableHeaderCell>Entiteit</TableHeaderCell>
                                )}
                                <TableHeaderCell
                                    sorted={isSorted('checked_at')}
                                    onClick={() =>
                                        updateFilters('sort', 'checked_at')
                                    }
                                >
                                    Laatst gecontroleerd op
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Laatste status
                                </TableHeaderCell>
                                {canDownload && (
                                    <TableHeaderCell textAlign='center'>
                                        Laatste schermafbeelding
                                    </TableHeaderCell>
                                )}
                                <TableHeaderCell textAlign='center'>
                                    Totaal aantal controles
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className='w-px'
                                    textAlign='right'
                                >
                                    Acties
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {dgrIdentities?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={columns} />
                            )}

                            {dgrIdentities?.data?.map(dgrIdentity => (
                                <TableRow key={dgrIdentity.id}>
                                    <TableCell>
                                        <EmployeeCard employee={dgrIdentity} />
                                    </TableCell>
                                    {showEntity && (
                                        <TableCell>
                                            {dgrIdentity.entity?.name}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        {localDate(
                                            dgrIdentity.latest_check?.checked_at
                                        )}
                                    </TableCell>
                                    <TableCell textAlign='center'>
                                        <StatusIcon
                                            status={
                                                dgrIdentity.latest_check?.status
                                            }
                                        />
                                    </TableCell>
                                    {canDownload && (
                                        <TableCell textAlign='center'>
                                            {dgrIdentity.latest_check
                                                ?.document_id && (
                                                <Button
                                                    as='a'
                                                    href={`/dgr-identity-check/${dgrIdentity.latest_check.id}/screenshot/download`}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    aria-label={`Schermafbeelding van ${dgrIdentity.name}`}
                                                >
                                                    <ArrowDownTrayIcon className='size-5' />
                                                </Button>
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell textAlign='center'>
                                        {dgrIdentity.checks_count}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='right'
                                    >
                                        {(dgrIdentity.checks_count > 0 ||
                                            canCheckDgr(dgrIdentity)) && (
                                            <Menu>
                                                <MenuButton
                                                    aria-label={`Acties voor ${dgrIdentity.name}`}
                                                >
                                                    <EllipsisVerticalIcon className='size-5' />
                                                </MenuButton>
                                                <MenuItems anchor='bottom end'>
                                                    {dgrIdentity.checks_count >
                                                        0 && (
                                                        <MenuItem
                                                            icon={EyeIcon}
                                                            onClick={() =>
                                                                setChecksModal(
                                                                    dgrIdentity.id
                                                                )
                                                            }
                                                        >
                                                            Tijdlijn
                                                        </MenuItem>
                                                    )}
                                                    {canCheckDgr(
                                                        dgrIdentity
                                                    ) && (
                                                        <MenuItem
                                                            icon={CpuChipIcon}
                                                            onClick={() =>
                                                                checkDgr(
                                                                    dgrIdentity
                                                                )
                                                            }
                                                        >
                                                            Controleer DGR
                                                        </MenuItem>
                                                    )}
                                                </MenuItems>
                                            </Menu>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
                <SegmentFooter>
                    <CustomPagination
                        meta={dgrIdentities?.meta}
                        only={['dgr_identities']}
                    />
                </SegmentFooter>
            </Segment>

            {checksModal !== null && (
                <ChecksModal
                    dgrIdentityId={checksModal}
                    onClose={() => setChecksModal(null)}
                />
            )}
        </Container>
    );
};

export default IndexView;
